import {
  useQueries,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo } from "react";

import {
  ChartData,
  OrgTimeSeriesMetricsData,
} from "../../../../models/ChartCollectionData";
import DataSvc from "../../../../services/dataSvc";
import { Timespan } from "../../../DataDownloadComponents/TimespanPicker/types";
import { IFacilityDetailsAnalysisChartOptions } from "../types";

type MetricQueriesOptions = UseQueryOptions<
  OrgTimeSeriesMetricsData & { metric: string }
>[];
type MetricQueriesResults = UseQueryResult<
  OrgTimeSeriesMetricsData & { metric: string }
>[];

type UseIndividualFacilityChartDetailsParams = {
  locationId: string;
  chartData: ChartData | undefined;
  timespan: Timespan;
  options: IFacilityDetailsAnalysisChartOptions;
};

/**
 * Takes in chart data,
 * fetches the dataset for the chart from the API,
 * and returns the dataset as needed by a LineChart component.
 * For use in the facility details page.
 * This hook is meant to untangle the complexity of the Metrics API calls, so this code is going to be messy.
 */
export const useIndividualFacilityChartDetails = ({
  locationId,
  chartData,
  timespan,
  options,
}: UseIndividualFacilityChartDetailsParams) => {
  const metrics: string[][] = JSON.parse(chartData?.metrics || "[]");

  const requestParams = {
    organization_id: null,
    start_date: timespan.startDate?.format("YYYY-MM-DD") || null,
    end_date: timespan.endDate?.format("YYYY-MM-DD") || null,
    municipality_id: chartData?.municipality_filter?.split(","),
    category_id: chartData?.category_filter?.split(","),
    label_id: chartData?.label_filter?.split(","),
    aggregation: chartData?.aggregation,
    group: [],
    type: chartData?.type,
    time_unit: chartData?.time_unit,
    location_id: [locationId],
  };

  const prevYearRequestParams = {
    ...requestParams,
    start_date: dayjs(requestParams.start_date)
      .subtract(1, "year")
      .format("YYYY-MM-DD"),
    end_date: dayjs(requestParams.end_date)
      .subtract(1, "year")
      .format("YYYY-MM-DD"),
  };

  const queries: MetricQueriesResults = useQueries<MetricQueriesOptions>({
    queries: metrics.map((metric) => ({
      queryKey: ["chartDetailsData", requestParams, metric],
      queryFn: async () => {
        // Attach the metric to the response so we can identify it later
        const response = await DataSvc.getChartDataByTimeSeries({
          ...requestParams,
          metric,
        });
        return { ...response, metric };
      },
      enabled: !!chartData,
    })),
  });

  const processedCurrentData: OrgTimeSeriesMetricsData[] = useMemo(() => {
    const currentData = queries.map((query) => query.data);
    if (!currentData) {
      return [];
    }
    return currentData.map((item) => ({
      title: [item?.metric ?? ""],
      organization_id: 0,
      start_date: item?.start_date ?? "",
      end_date: item?.end_date ?? "",
      data: item?.data || [],
    }));
  }, [queries]);

  const prevYearQueries: MetricQueriesResults =
    useQueries<MetricQueriesOptions>({
      queries: metrics.map((metric) => ({
        queryKey: ["chartDetailsData", prevYearRequestParams, metric],
        queryFn: async () => {
          // Attach the metric to the response so we can identify it later
          const response = await DataSvc.getChartDataByTimeSeries({
            ...prevYearRequestParams,
            metric,
          });
          return { ...response, metric };
        },
        enabled: !!chartData && options.compareToPrev,
      })),
    });

  const processedPrevYearData: OrgTimeSeriesMetricsData[] = useMemo(() => {
    const prevYearData = prevYearQueries.map((query) => query.data);
    if (!prevYearData) {
      return [];
    }
    return prevYearData.map((item) => ({
      title: [item?.metric ?? ""],
      organization_id: 0,
      start_date: dayjs(item?.start_date).add(1, "year").format("YYYY-MM-DD"),
      end_date: dayjs(item?.end_date).add(1, "year").format("YYYY-MM-DD"),
      data: item?.data || [],
    }));
  }, [prevYearQueries]);

  const isFetching =
    queries.some((query) => query.isFetching) ||
    prevYearQueries.some((query) => query.isFetching);

  return {
    currentData: processedCurrentData,
    prevYearData: processedPrevYearData,
    isFetching,
  };
};
